import * as yup from 'yup';

export enum AddonType {
  EvalQualifyInXTradingDays = 'eval_quality_x_days',
  EvalReducedPaLifetimeFee = 'eval_reduced_pa_fee',
  EvalDiscountXPercentReset = 'eval_discount_x_reset',
  PaAllowExtraAmountOnPayout = 'pa_extra_amount',
  PaPayoutEveryXDays = 'pa_payout_x_days',
  PaChange30PercentRuleToXPercent = 'pa_change_30pct_rule',
  StraightToPa = 'straight_to_pa',
  PerformanceAccountFee = 'performance_account_fee',
  AccountBundle = 'account_bundle',
  Drawdown = 'drawdown',
}

export enum AddonExtraAllowPayoutTime {
  ALL = 'all',
  FIRST_TIME = '1st',
  SECOND_TIME = '2nd',
}

export enum AddonPerformanceAccountFee {
  MONTHLY = 'performance_fee_monthly',
  LIFETIME_ONE_TIME = 'performance_fee_lifetime_one_time',
  LIFETIME_PAY_UPFRONT = 'performance_fee_lifetime_pay_upfront',
  LIFETIME_PAY_PARTIAL = 'performance_fee_lifetime_pay_partial',
}

export enum AddonDrawdown {
  NONE = '0',
  PLUS_500 = '500',
  PLUS_1000 = '1000',
  PLUS_1500 = '1500',
}

export type Product = {
  id?: number;
  title?: string | null;
  description?: string | null;
  internal_notes?: string | null;
  display_name_web?: string | null;
  display_name_app?: string | null;
  required_product_ids?: number[] | null;
  disallow_product_ids?: number[] | null;
  start_date_calculation?: string | null;
  mailchimp_list_segments?: string[] | null;
  disable_product_link?: boolean | null;
  max_payouts_per_account?: number | null;
  category?: string | null;
  type?: string | null;
  is_bundle?: boolean | null;
  account_benefits?: string | null;
  accounts_in_bundle?: number | null;
  weekly_account_payout_max?: number | null;
  account_type?: string | null;
  competition_activation_date?: string | null;
  payout_date_eligible_type?: string | null;
  is_performance_account?: boolean | null;
  is_visible_on_web?: boolean | null;
  compatible_data_feeds?: string[] | null;
  cash_in_hand?: number | null;
  auto_liquidate?: number | null;
  drawdown?: number | null;
  drawdown_type?: string | null;
  trailing_drawdown_stops?: boolean | null;
  trailing_drawdown_stop_at?: number | null;
  pa_account_trailing_drawdown_stop_at?: number | null;
  days_to_qualify?: number | null;
  profit_target?: number | null;
  fill_out_survey?: boolean | null;
  survey_trigger_time?: string | null;
  offer_reset_practice_account_phase?: boolean | null;
  offer_reset_performance_account_phase?: boolean | null;
  action_after_payout?: string | null;
  payout_request_trigger?: string | null;
  practice_contract_usage_enabled?: boolean | null;
  practice_contract_not_exceed_percent?: number | null;
  practice_contract_average_days?: number | null;
  practice_profit_not_exceed_percent?: number | null;
  practice_profit_average_days?: number | null;
  practice_best_day_profit_not_exceed_percent?: number | null;
  practice_best_day_profit?: number | null;
  performance_contract_not_exceed_percent?: number | null;
  performance_contract_average_days?: number | null;
  performance_profit_not_exceed_percent?: number | null;
  performance_profit_average_days?: number | null;
  performance_best_day_profit_not_exceed_percent?: number | null;
  performance_best_day_profit?: number | null;
  practice_profit_withdrawal_enabled?: boolean | null;
  practice_best_day_profit_enabled?: boolean | null;
  performance_contract_usage_enabled?: boolean | null;
  performance_profit_withdrawal_enabled?: boolean | null;
  performance_best_day_profit_enabled?: boolean | null;
  addon_type?: string | null;
  addon_lifetime_performance_account_package_id?: number | null;
  addon_trading_days_required?: number | null;
  addon_discount_percent?: number | null;
  addon_valid_for_days?: number | null;
  addon_extra_amount_allowed_payout?: number | null;
  addon_extra_allowed_payout_time?: string | null;
  addon_additional_drawdown_option?: number | null;
  addon_new_percent_override_default?: number | null;
  addon_disable_percent_default?: boolean | null;
  allow_reset_of_packages?: number[] | null;
  market_data_type?: string | null;
  performance_account_type?: string | null;
  addon_packages?: number[] | null;
  addon_disable_at?: string | null;
  stripe_product_id?: string | null;
  paypal_product_id?: string | null;
  tradovate_financial_template?: number | null;
  dxfeed_financial_template?: string | null;
  addon_performance_account_fee_option?: string | null;
  affiliate_eligible?: boolean | null;
};

export type BillingPlan = {
  name?: string | null;
  currency?: string | null;
  first_price?: number | null;
  addon_monthly_price?: number | null;
  product_id?: number;
  first_period_value?: number | null;
  first_period_unit?: string | null;
  rebill_times_type?: string | null;
  rebill_times_unit?: number | null;
  second_price?: number | null;
  second_period_value?: number | null;
  second_period_unit?: string | null;
  quantity?: number | null;
  change_quantity?: number | null;
  expire_in_last_day_of_current_month?: boolean;
  is_apply_convenience_fee?: boolean;
  is_apply_tax?: boolean;
  is_enabled?: boolean;
  stripe_plan_id?: string;
  paypal_plan_id?: string;
};

export type PayoutLogic = {
  product_id?: number | null;
  min_payout?: number | null;
  max_payout?: number | null;
  payout_request_after_days?: number | null;
  split_start_amount?: number | null;
  split_percentage?: number | null;
  apply_to_copy_account?: boolean;
  copy_account_amount?: number | null;
  daily_profit_required?: boolean;
  daily_profit_required_amount?: number | null;
  required_balance_threshold_max?: number | null;
  id?: number;
};

export type Instrument = {
  product_id?: number | null;
  rithmic_standard_package?: string | null;
  rithmic_performance_package?: string | null;
  package_rms_content_standard?: string | null;
  package_rms_content_performance?: string | null;
};

export type RithmicInstrument = {
  id?: number;
  product_id?: number;
  rithmic_csv_package?: string;
  type?: string;
  created_at?: string;
};

export type ProductData = {
  product?: Product;
  billing_plan?: BillingPlan;
  payout_logics?: PayoutLogic[] | null;
  instrument?: Instrument | null;
};

export type ProductOffer = Partial<{
  account_benefits: string | null;
  account_type: string | null;
  competition_activation_date: string | null;
  payout_date_eligible_type: string | null;
  accounts_in_bundle: number | null;
  weekly_account_payout_max: number | null;
  action_after_payout: string | null;
  addon_disable_at: string | null;
  addon_disable_percent_default: boolean | null;
  addon_discount_percent: number | null;
  addon_extra_allowed_payout_time: string | null;
  addon_extra_amount_allowed_payout: number | null;
  addon_additional_drawdown_option: number | null;
  addon_lifetime_performance_account_package_id: number | null;
  addon_new_percent_override_default: number | null;
  addon_performance_account_fee_option: string | null;
  addon_packages: number[] | null;
  addon_trading_days_required: number | null;
  addon_type: string | null;
  addon_valid_for_days: number | null;
  allow_reset_of_packages: number[] | null;
  auto_liquidate: number | null;
  billing_plan: BillingPlan | null;
  cash_in_hand: number | null;
  category: string | null;
  compatible_data_feeds: string[] | null;
  days_to_qualify: number | null;
  description: string | null;
  disable_product_link: boolean | null;
  disallow_product_ids: number[] | null;
  display_name_app: string | null;
  display_name_web: string | null;
  drawdown: number | null;
  drawdown_type: string | null;
  fill_out_survey: boolean | null;
  id: number;
  internal_notes: string | null;
  is_active: boolean | null;
  is_performance_account: boolean | null;
  mailchimp_list_segments: string[] | null;
  market_data_type: string | null;
  performance_account_type: string | null;
  max_payouts_per_account: number | null;
  offer_reset_practice_account_phase: boolean | null;
  offer_reset_performance_account_phase: boolean | null;
  payout_logics: PayoutLogic[] | null;
  payout_request_trigger: string | null;
  performance_best_day_profit_enabled: boolean | null;
  performance_contract_usage_enabled: boolean | null;
  performance_profit_withdrawal_enabled: boolean | null;
  practice_best_day_profit_enabled: boolean | null;
  practice_contract_usage_enabled: boolean | null;
  practice_profit_withdrawal_enabled: boolean | null;
  profit_target: number | null;
  required_product_ids: number[] | null;
  start_date_calculation: string | null;
  survey_trigger_time: string | null;
  subscribe_users: number | null;
  title: string | null;
  trailing_drawdown_stops: boolean | null;
  trailing_drawdown_stop_at: number | null;
  pa_account_trailing_drawdown_stop_at: number | null;
  type: string | null;
  is_bundle: boolean | null;
  is_visible_on_web: boolean | null;
  instrument: Instrument | null;
  tradovate_financial_template?: number | null;
  dxfeed_financial_template?: string | null;
  rithmic_instruments?: RithmicInstrument[];
  practice_contract_not_exceed_percent: number | null;
  practice_contract_average_days: number | null;
  practice_profit_not_exceed_percent: number | null;
  practice_profit_average_days: number | null;
  practice_best_day_profit_not_exceed_percent: number | null;
  practice_best_day_profit: number | null;
  performance_contract_not_exceed_percent: number | null;
  performance_contract_average_days: number | null;
  performance_profit_not_exceed_percent: number | null;
  performance_profit_average_days: number | null;
  performance_best_day_profit_not_exceed_percent: number | null;
  performance_best_day_profit: number | null;
  affiliate_eligible: boolean | null;
}>;

export type ProductAddonsReq = Partial<{
  title: string;
  price: number;
  addon_lifetime_performance_account_package_id: number;
  addon_trading_days_required: number;
  addon_discount_percent: number;
  addon_valid_for_days: number;
  addon_extra_amount_allowed_payout: number;
  addon_new_percent_override_default: number;
  addon_extra_allowed_payout_time: string;
  addon_disable_percent_default: boolean;
  addon_packages: number[];
  addon_additional_drawdown_option: number;
  addon_performance_account_fee_option: string;
}>;

export type ProductResetReq = Partial<{
  title: string;
  price: number;
  allow_reset_of_packages: number[];
}>;

export type ProductMarketDataReq = Partial<{
  title: string;
  price: number;
  market_data_type: string;
}>;

export const schemaProduct = yup.object({
  title: yup.string().required('Field required'),
  description: yup.string().required('Field required'),
  internal_notes: yup.string().optional(),
  display_name_web: yup.string().optional(),
  display_name_app: yup.string().optional(),
  required_product_ids: yup.array().optional().nullable(),
  disallow_product_ids: yup.array().optional().nullable(),
  start_date_calculation: yup.string().required('Field required'),
  mailchimp_list_segments: yup.array().optional().nullable(),
  disable_product_link: yup.boolean().optional().nullable(),
  is_visible_on_web: yup.boolean().optional().nullable(),
  category: yup.string().required('Field required'),
  // type of product
  type: yup.string().optional(),
  weekly_account_payout_max: yup.number().optional(),
  //
  account_type: yup.string().optional(),
  competition_activation_date: yup.string().when('account_type', {
    is: 'specialty_account',
    then: (schema) =>
      schema.required('Activation date is required for Specialty Account'),
    otherwise: (schema) => schema.optional(),
  }),
  payout_date_eligible_type: yup
    .string()
    .nullable()
    .when('payout_request_trigger', {
      is: 'date_eligible',
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  is_bundle: yup.boolean().optional(),
  accounts_in_bundle: yup.number().optional(),
  drawdown_type: yup.string().optional(),
  is_performance_account: yup.boolean().optional().nullable(),
  compatible_data_feeds: yup.array().optional().nullable(),
  cash_in_hand: yup.number().optional(),
  auto_liquidate: yup.number().optional(),
  drawdown: yup.number().optional(),
  trailing_drawdown_stops: yup.boolean().optional(),
  trailing_drawdown_stop_at: yup.number().optional(),
  pa_account_trailing_drawdown_stop_at: yup.number().optional(),
  days_to_qualify: yup.number().optional(),
  profit_target: yup.number().optional(),
  fill_out_survey: yup.boolean().optional(),
  survey_trigger_time: yup.string().optional(),
  offer_reset_practice_account_phase: yup.boolean().optional(),
  offer_reset_performance_account_phase: yup.boolean().optional(),
  action_after_payout: yup.string().optional(),
  payout_request_trigger: yup.string().optional(),
  practice_contract_usage_enabled: yup.boolean().optional(),
  practice_contract_not_exceed_percent: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('practice_contract_usage_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  practice_contract_average_days: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('practice_contract_usage_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  practice_profit_withdrawal_enabled: yup.boolean().optional(),
  practice_profit_not_exceed_percent: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('practice_profit_withdrawal_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  practice_profit_average_days: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('practice_profit_withdrawal_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  practice_best_day_profit_enabled: yup.boolean().optional(),
  practice_best_day_profit_not_exceed_percent: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('practice_best_day_profit_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  practice_best_day_profit: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('practice_best_day_profit_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  performance_contract_usage_enabled: yup.boolean().optional(),
  performance_contract_not_exceed_percent: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('performance_contract_usage_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  performance_contract_average_days: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('performance_contract_usage_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  performance_profit_withdrawal_enabled: yup.boolean().optional(),
  performance_profit_not_exceed_percent: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('performance_profit_withdrawal_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  performance_profit_average_days: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('performance_profit_withdrawal_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  performance_best_day_profit_enabled: yup.boolean().optional(),
  performance_best_day_profit_not_exceed_percent: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('performance_best_day_profit_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  performance_best_day_profit: yup
    .number()
    .transform((value, originalValue) =>
      originalValue === '' ? undefined : value
    )
    .when('performance_best_day_profit_enabled', {
      is: true,
      then: (schema) => schema.required('Field required'),
      otherwise: (schema) => schema.optional(),
    }),
  max_payouts_per_account: yup.number().optional(),
  //addons type
  addon_type: yup.string().optional(),
  account_benefits: yup.string().optional(),
  addon_lifetime_performance_account_package_id: yup.string().optional(),
  addon_trading_days_required: yup.number().optional(),
  addon_discount_percent: yup.number().optional(),
  addon_valid_for_days: yup.number().optional(),
  addon_extra_amount_allowed_payout: yup.number().optional(),
  addon_extra_allowed_payout_time: yup.string().optional(),
  addon_performance_account_fee_option: yup.string().optional(),
  addon_additional_drawdown_option: yup.string().optional(),
  addon_new_percent_override_default: yup.number().optional(),
  addon_disable_percent_default: yup.boolean().optional(),
  //resets
  allow_reset_of_packages: yup.array().optional().nullable(),
  //market data
  market_data_type: yup.string().optional(),
  performance_account_type: yup.string().optional(),
  //
  payout_logics: yup
    .array()
    .of(
      yup.object().shape({
        min_payout: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .nullable()
          .required('Field required'),
        max_payout: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .nullable()
          .required('Field required'),
        payout_request_after_days: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .nullable()
          .required('Field required'),
        split_start_amount: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .nullable()
          .required('Field required'),
        split_percentage: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .nullable()
          .required('Field required'),
        apply_to_copy_account: yup.boolean().required('Field required'),
        copy_account_amount: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .nullable()
          .when('apply_to_copy_account', {
            is: true,
            then: (schema) => schema.required('Field required'),
            otherwise: (schema) => schema.optional(),
          }),
        daily_profit_required: yup.boolean().required('Field required'),
        daily_profit_required_amount: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .nullable()
          .when('daily_profit_required', {
            is: true,
            then: (schema) => schema.required('Field required'),
            otherwise: (schema) => schema.optional(),
          }),
        required_balance_threshold_max: yup
          .number()
          .transform((value, originalValue) =>
            originalValue === '' ? undefined : value
          )
          .nullable()
          .required('Field required'),
      })
    )
    .optional(),
  currency: yup.string().required('Field required'),
  name: yup.string().required('Field required'),
  first_price: yup.number().required('Field required'),
  first_period_value: yup
    .number()
    .nullable()
    .when('first_period_unit', {
      is: 'LIFETIME',
      then: (schema) => schema.optional(),
      otherwise: (schema) => schema.required('Field required'),
    }),
  first_period_unit: yup.string().required('Field required'),
  second_price: yup.number().optional(),
  addon_monthly_price: yup.number().optional(),
  second_period_value: yup.number().optional(),
  second_period_unit: yup.string().optional(),
  rebill_times_type: yup.string().optional().nullable(),
  rebill_times_unit: yup.number().optional(),
  quantity: yup.number().required('Field required').nullable(),
  change_quantity: yup.number().required('Field required').nullable(),
  expire_in_last_day_of_current_month: yup.boolean().optional(),
  is_apply_tax: yup.boolean().optional(),
  is_apply_convenience_fee: yup.boolean().optional(),
  is_enabled: yup.boolean().optional(),
  company: yup.string().optional(),
  rithmic_standard_package: yup.string().optional(),
  rithmic_performance_package: yup.string().optional(),
  package_rms_content_standard: yup.string().optional(),
  package_rms_content_performance: yup.string().optional(),
  tradovate_financial_template: yup.string().optional(),
  dxfeed_financial_template: yup.string().optional(),
  affiliate_eligible: yup.boolean().optional(),
});

export type ProductFormData = yup.InferType<typeof schemaProduct>;

export const schemaAddons = yup.object({
  title: yup.string().required('Field Required'),
  price: yup.number().required('Field Required'),
  addon_packages: yup.array().optional(),
  addon_type: yup.string().optional(),
  addon_discount_percent: yup.number().optional(),
  addon_valid_for_days: yup.number().optional(),
  addon_lifetime_performance_account_package_id: yup.string().optional(),
  addon_trading_days_required: yup.number().optional(),
  addon_extra_amount_allowed_payout: yup.number().optional(),
  addon_extra_allowed_payout_time: yup.string().optional(),
  addon_new_percent_override_default: yup.number().optional(),
  addon_disable_percent_default: yup.boolean().optional(),
  accounts_in_bundle: yup.number().optional(),
  addon_performance_account_fee_option: yup.string().optional(),
  drawdown_type: yup.string().optional(),
  addon_additional_drawdown_option: yup.string().optional(),
});

export type AddonFormData = yup.InferType<typeof schemaAddons>;

export const schemaResets = yup.object({
  title: yup.string().required('Field Required'),
  price: yup.number().required('Field Required'),
  allow_reset_of_packages: yup.array().required('Field Required'),
});

export type ResetFormData = yup.InferType<typeof schemaResets>;

export const schemaMarketData = yup.object({
  title: yup.string().required('Field Required'),
  price: yup.number().required('Field Required'),
  market_data_type: yup.string().required('Field Required'),
});

export type MarketDataFormData = yup.InferType<typeof schemaMarketData>;

export type AddonTypeData = Pick<
  AddonFormData,
  | 'addon_trading_days_required'
  | 'addon_lifetime_performance_account_package_id'
  | 'addon_type'
  | 'addon_extra_amount_allowed_payout'
  | 'addon_new_percent_override_default'
  | 'addon_disable_percent_default'
  | 'addon_discount_percent'
  | 'addon_valid_for_days'
  | 'addon_extra_allowed_payout_time'
  | 'accounts_in_bundle'
  | 'addon_performance_account_fee_option'
  | 'drawdown_type'
  | 'addon_additional_drawdown_option'
>;

export enum YesOrNo {
  Yes = 'yes',
  No = 'no',
}

export enum PeriodUnit {
  Day = 'DAY',
  Month = 'MONTH',
  Year = 'YEAR',
  LIFETIMES = 'LIFETIME',
}

export enum ReBillTimesType {
  NoMoreCharges = 'no_more_charges',
  ChargeSecondPriceOnce = 'charge_second_price_once',
  ChargeSecondPriceTimes = 'charge_second_price_x_times',
  ReBillSecondPriceUntilCancelled = 're_bill_second_price_until_cancelled',
}

export enum StartDateCalculation {
  LAST_EXISTING_SUBSCRIPTION_DATE_OF_THIS_PRODUCT = 'last_existing_subscription_date_of_this_product',
  LAST_EXPIRATION_DATE_IN_THE_RENEWAL_GROUP = 'last_expiration_date_in_the_renewal_group',
  PAYMENT_DATE = 'payment_date',
  FIXED_DATE = 'fixed_date',
  NEXT_DAY = 'next_day',
  FIRST_DAY_OF_CURRENT_MONTH = '1st_of_current_month',
  LAST_OF_CURRENT_MONTH = 'last_of_current_month',
  NEXT_OCCURRENCE_OF_SUNDAY = 'next_occurrence_of_sunday',
  NEXT_OCCURRENCE_OF_MONDAY = 'next_occurrence_of_monday',
  NEXT_OCCURRENCE_OF_TUESDAY = 'next_occurrence_of_tuesday',
  NEXT_OCCURRENCE_OF_WEDNESDAY = 'next_occurrence_of_wednesday',
  NEXT_OCCURRENCE_OF_THURSDAY = 'next_occurrence_of_thursday',
  NEXT_OCCURRENCE_OF_FRIDAY = 'next_occurrence_of_friday',
  NEXT_OCCURRENCE_OF_SATURDAY = 'next_occurrence_of_saturday',
  NEXT_OCCURRENCE_OF_1ST_DAY_OF_MONTH = 'next_occurrence_of_1st_day_of_month',
  NEXT_OCCURRENCE_OF_15th_DAY_OF_MONTH = 'next_occurrence_of_15th_day_of_month',
}

export enum ProductTypes {
  TRADING_ACCOUNT = 'trading_account',
  ADD_ONS_EDGE_ENHANCERS = 'add_ons_edge_enhancers',
  RESETS = 'reset',
  MARKET_DATA = 'market_data',
  OTHER = 'other',
}

export enum MarketDataTypes {
  CME_MARKET_DEPTH = 'cme_market_depth',
  CBOT_MARKET_DEPTH = 'cbot_market_depth',
  COMEX_MARKET_DEPTH = 'comex_market_depth',
  NYMEX_MARKET_DEPTH = 'nymex_market_depth',
  MARKET_DEPTH_4_BUNDLE = 'market_depth_4_bundle',
}
export enum PerformanceAccountType {
  PERFORMANCE_ACCOUNT_MONTHLY = 'pa_monthly',
  PERFORMANCE_ACCOUNT_ONE_TIME_FEE = 'pa_one_time',
}

export enum ProductTradingAccountTypes {
  STRAIGHT_TO_PA = 'straight_to_pa',
  PRACTICE_ACCOUNT = 'practice_account',
  COMPETITION = 'competition',
  DEMO_ACCOUNT = 'demo_account',
  SPECIALTY_ACCOUNT = 'specialty_account',
}
export enum ProductAccountTypes {
  FOUNDATION = 'foundation',
  ENTRY = 'entry',
  BUNDLE = 'bundles',
  WEEKLY = 'weekly',
  STRAIGHT_TO_PA = 'straight_to_pa',
  PRACTICE_ACCOUNT = 'practice_account',
  SPECIALTY_ACCOUNT = 'specialty_account',
}
export type ProductOption = {
  id: number;
  title: string;
};
